/*
  Lädt den Plan eines Zeitraums, der über die angezeigte Periode hinausgeht.

  Das Raster zeigt genau eine Periode und lädt auch nur diese — sonst käme bei
  jedem Speichern das ganze Archiv über die Leitung. Ein paar Ansichten
  brauchen aber mehr: die Statistik ein Kalenderjahr, "Periode kopieren" eine
  frei gewählte Quellperiode, der Export einen gewählten Bereich, die Freigabe
  alle offenen Entwürfe.

  Sie holen sich ihren Ausschnitt beim Öffnen. Solange er unterwegs ist, gilt
  `fallback` — die bereits geladene Periode. Damit steht nie ein leeres
  Fenster da, und was innerhalb der Periode liegt, stimmt sofort.
*/

import { useEffect, useMemo, useRef, useState } from "react";

const EMPTY_RANGE = Object.freeze({ shifts: [], userHolidays: [], publicHolidays: [], dayComments: [] });

export default function usePlanRange({
  enabled = true,
  startDate = "",
  endDate = "",
  draftsOnly = false,
  onLoadPlanRange,
  fallbackShifts = [],
  fallbackUserHolidays = [],
  fallbackPublicHolidays = [],
}) {
  const [range, setRange] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestRef = useRef(0);

  const canLoad = Boolean(
    enabled && typeof onLoadPlanRange === "function" && (draftsOnly || (startDate && endDate))
  );

  useEffect(() => {
    if (!canLoad) {
      // Beim Schliessen zurücksetzen: Sonst zeigt das nächste Öffnen für einen
      // Wimpernschlag den Zeitraum von vorhin.
      setRange(null);
      setError(null);
      return undefined;
    }

    const requestId = (requestRef.current += 1);
    let isCurrent = true;
    setIsLoading(true);
    setError(null);

    Promise.resolve(onLoadPlanRange({ startDate, endDate, draftsOnly }))
      .then((result) => {
        if (!isCurrent || requestId !== requestRef.current) return;
        setRange(result || EMPTY_RANGE);
      })
      .catch((loadError) => {
        if (!isCurrent || requestId !== requestRef.current) return;
        setRange(null);
        setError(loadError);
      })
      .finally(() => {
        if (isCurrent && requestId === requestRef.current) setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [canLoad, draftsOnly, endDate, onLoadPlanRange, startDate]);

  return useMemo(
    () => ({
      shifts: range ? range.shifts : fallbackShifts,
      userHolidays: range ? range.userHolidays : fallbackUserHolidays,
      publicHolidays: range ? range.publicHolidays : fallbackPublicHolidays,
      // Wahr, solange die Zahlen noch die der angezeigten Periode sind.
      isLoading,
      isFallback: !range,
      error,
    }),
    [error, fallbackPublicHolidays, fallbackShifts, fallbackUserHolidays, isLoading, range]
  );
}
